/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, type Type, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
type Rest, ActionAbstract, FormControl, FormGroup, I18nService,
SelectComponent, UNPLACEHOLDER, UtilService } from '@dm/core';
import { ToolService } from 'app/common/services';
import { type AvTask, type OnAccessPolicy, type OnDemandPolicy } from 'app/common/types/mo';
import { merge } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { AntivirusService } from '../../../antivirus.service';
import { AntivirusStrService, DefaultOnAccessPolicyName, DurationTypeE, ExcludeFileE, ImmediatelyDurationBoundaryE, ImmediatelyTaskE, ScanMissionEntryTypeE, ScanPolicyTypeE } from '../../../antivirus-str.service';
import { FilePathRenderEntryE } from '../../file-path-table-render/file-path-table-render.component';
import { CreateOnAccessScanComponent } from '../../on-access-scan/create-on-access-scan/create-on-access-scan.component';
import { CreateOnDemandScanComponent } from '../../on-demand-scan/create-on-demand-scan/create-on-demand-scan.component';
import { TypePathDetailTableComponent } from '../../type-path-detail-table/type-path-detail-table.component';
@Component({
    selector: 'app-create-fs-scan-mission-policy-card',
    templateUrl: './create-fs-scan-mission-policy-card.component.html',
    styleUrls: ['./create-fs-scan-mission-policy-card.component.scss'],
})

export class CreateFsScanMissionPolicyCardComponent extends ActionAbstract implements OnInit, OnDestroy {
    @ViewChild('scanPolicySelect') scanPolicySelect: SelectComponent;
    // 外部传入的vm
    @Input() vm: FormGroup;
    @Input() type: ScanPolicyTypeE;
    @Input() entry: ScanMissionEntryTypeE = ScanMissionEntryTypeE.CREATE;
    @Input() vstoreId: string;
    @Input() isTemplateCreate: boolean;
    @Input() fsId: string;
    i18n = this.str.i18n;
    UNPLACEHOLDER = UNPLACEHOLDER;
    ScanPolicyTypeE = ScanPolicyTypeE;
    DurationTypeE = DurationTypeE;
    ScanMissionEntryTypeE = ScanMissionEntryTypeE;
    FilePathRenderEntryE = FilePathRenderEntryE;
    ExcludeFileE = ExcludeFileE;
    scanPolicyRest: Rest;
    errorMessage: any;
    // 开关状态, 默认true
    switch = true;
    // 选中的策略，默认null
    selectPolicyOpt = null;
    // 扫描策略标题
    scanPolicyTitle: string;
    // 卡片提示信息
    cardTips: string;
    switchChanged = false;
    onDemandFilePathMaxWidth = '';
    onAccessFilePathMaxWidth = '';
    /**
     * 是否是中文
     */
    isZh = this.i18nService.language === 'zh';
    getLabelFromOptions = this.utilService.getLabelFromOptions;

    constructor(
        injector: Injector,
        public str: AntivirusStrService,
        public i18nService: I18nService,
        private service: AntivirusService,
        private toolService: ToolService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.initCard();
        this.initVm();
        this.handldeUsedInFs();
    }

    /**
     * 初始化卡片必要的参数
     */
    initCard(): void {
        if (this.type === ScanPolicyTypeE.ONDEMAND) {
            this.scanPolicyTitle = this.i18n.onDemandScan;
            this.cardTips = this.i18n.policyCardOnDemandTip;
        } else {
            this.scanPolicyTitle = this.i18n.onAccessScan;
            this.cardTips = this.i18n.policyCardOnAccessTip;
        }

        // 中英文、不同入口进入，最大宽度不同
        if (this.entry === ScanMissionEntryTypeE.CREATE) {
            if (this.isZh) {
                this.onDemandFilePathMaxWidth = '5.5rem';
                this.onAccessFilePathMaxWidth = '5.5rem';
            } else {
                this.onDemandFilePathMaxWidth = '5.4rem';
                this.onAccessFilePathMaxWidth = '5.1rem';
            }
        } else {
            if (this.isZh) {
                this.onDemandFilePathMaxWidth = '5rem';
                this.onAccessFilePathMaxWidth = '5rem';
            } else {
                this.onDemandFilePathMaxWidth = '4.9rem';
                this.onAccessFilePathMaxWidth = '4.6rem';
            }
        }

        this.scanPolicyRest = this.type === ScanPolicyTypeE.ONDEMAND ?
            this.service.getBatchOnDemandPolicyRest(this.vstoreId) :
            this.service.getBatchOnAccessPolicyRest(this.vstoreId);
    }

    initVm(): void {
        this.vm.addControl('selectedScanPolicy' + this.type, new FormControl(null, [Validators.required]));

        if (this.type === ScanPolicyTypeE.ONDEMAND && this.entry === ScanMissionEntryTypeE.CREATE) {
            this.vm.addControl('immediately', new FormControl(false));
            this.vm.addControl('durationType', new FormControl(DurationTypeE.CUSTOM));
            this.vm.addControl('durationHour', new FormControl(this.str.defaultDurationHour));
            this.vm.addControl('durationMinute', new FormControl(this.str.defaultDurationMinute));
            this.detectDurationChange('durationHour', 'durationMinute');
        }

        this.errorMessage = {
            ['selectedScanPolicy' + this.type]: {
                required: this.toolService.requiredErr(this.i18n.policyLab),
            },
        };
    }

    /**
     * 检测持续时间变化
     */
    detectDurationChange(hourCtrlName: string, minuteCtrlName: string): void {
        merge(
            this.vm.get(hourCtrlName).valueChanges.pipe(distinctUntilChanged()),
            this.vm.get(minuteCtrlName).valueChanges.pipe(distinctUntilChanged()),
        ).pipe(debounceTime(10)).subscribe(() => {
            this.onDurationTimeChange(hourCtrlName, minuteCtrlName);
        });
    }

    /**
     * 持续时间变化，持续时间范围30分钟-168小时
     */
    onDurationTimeChange(hourCtrlName: string, minuteCtrlName: string): void {
        const minute = this.vm.get(minuteCtrlName).value;
        const hour = this.vm.get(hourCtrlName).value;

        if (minute < ImmediatelyDurationBoundaryE.MIN && hour === ImmediatelyDurationBoundaryE.ZERO_VALUE) {
            this.vm.get(minuteCtrlName).setValue(ImmediatelyDurationBoundaryE.MIN);
        }

        if (hour === ImmediatelyDurationBoundaryE.MAX) {
            this.vm.get(minuteCtrlName).setValue(ImmediatelyDurationBoundaryE.ZERO_VALUE);
        }
    }

    // 开关被切换的时候，需要disable一下
    onSwitchChange($event: boolean): void {
        this.switchChanged = true;

        if ($event) {
            this.vm.get('selectedScanPolicy' + this.type).enable();
            // 下一个周期去渲染下拉
            setTimeout(() => {
                this.scanPolicySelect.setFixedOption(this.selectPolicyOpt);
            });
        } else {
            this.vm.get('selectedScanPolicy' + this.type).disable();
        }
    }

    scanPolicySelectChange($event): void {
        // 先更新所选中的扫描策略
        this.selectPolicyOpt = $event.option;
    }

    /**
     * 下拉默认选中新建项
     *
     * @param {*} data 后台返回的原始对象
     *
     */
    addPolicyAndSelect(data: OnDemandPolicy): void {
        // 增加选项
        const newOption = {
            label: data.name,
            value: data.ID,
            data,
        };
        this.scanPolicySelect.setFixedOption(newOption);
    }

    createPolicy(): void {
        let targetComponent: Type<ActionAbstract>;
        targetComponent = this.type === ScanPolicyTypeE.ONDEMAND ?
            CreateOnDemandScanComponent :
            CreateOnAccessScanComponent;

        this.doAction(targetComponent, {
            vstoreId: this.vstoreId,
            onClose: ({ data }) => {
                if (!data) {
                    return;
                }

                this.addPolicyAndSelect(data);
            },
        });
    }

    /**
     * 处理该组件在文件系统创建中使用的场景
     *
     */
    handldeUsedInFs(): void {
        // 处理在文件系统调用的场景，只有从文件系统进来才有vstoreId
        if (!this.vstoreId) {
            return;
        }

        // 模板创建，查该文件系统的策略详情，来自动填充
        if (this.isTemplateCreate) {
            this.setTemplateCratePolicy();
            return;
        }
        this.setPolicyConfig();
    }


    /**
     * 设置表单项的回填
     *
     */
    setPolicyConfig(): void {
        if (this.type === ScanPolicyTypeE.ONACCESS) {
            this.setOnAccessPolicyConfig(this.vstoreId);
            return;
        }
        this.setOnDemandPolicyConfig(this.vstoreId);
    }

    /**
     * 设置模板创建文件系统场景的表单项
     *
     */
    setTemplateCratePolicy(): void {
        this.service
            .getAvTask(this.fsId, this.vstoreId, { doException: false })
            .subscribe(({ data }: { data: AvTask }) => {
                if (this.type === ScanPolicyTypeE.ONACCESS) {
                    const { onaccessPolicyId } = data;
                    if (onaccessPolicyId) {
                        this.service.getOnAccessPolicy(onaccessPolicyId, this.vstoreId).subscribe((res: {data: OnAccessPolicy}) => {
                            this.addPolicyAndSelect(res.data);
                        });
                        return;
                    }
                    this.controlScanSwitch(false);
                }

                if (this.type === ScanPolicyTypeE.ONDEMAND) {
                    const { ondemandPolicyId, isImmediatelyTask, immediatelyDuration } = data;
                    if (ondemandPolicyId) {
                        this.service.getOnDemandPolicy(ondemandPolicyId, this.vstoreId).subscribe((res: {data: OnDemandPolicy}) => {
                            this.addPolicyAndSelect(res.data);
                        });
                        this.setOnDemandRestPolicy(isImmediatelyTask, immediatelyDuration);
                        return;
                    }
                    this.controlScanSwitch(false);
                }
            }, () => {
                // 没查到报错，走普通创建的填充流程
                this.setPolicyConfig();
            });
    }

    /**
     * 设置按需扫描的剩余表单项
     *
     */
    setOnDemandRestPolicy(isImmediatelyTask: string | number, immediatelyDuration: string): void {
        if (isImmediatelyTask !== ImmediatelyTaskE.ENABLE) {
            return;
        }

        this.vm.get('immediately').patchValue(true);

        if (immediatelyDuration === DurationTypeE.UNTILFINISHED) {
            this.vm.get('durationType').patchValue(immediatelyDuration);
        } else {
            this.vm.get('durationType').patchValue(DurationTypeE.CUSTOM);
            const tempTime = immediatelyDuration.split(':');
            this.vm.get('durationHour').patchValue(this.hindleTimeWithZeroPrefix(tempTime[0]));
            this.vm.get('durationMinute').patchValue(this.hindleTimeWithZeroPrefix(tempTime[1]));
        }
    }

    /**
     * 处理0前缀的返回时间
     * '08' => '8'
     *
     */
    hindleTimeWithZeroPrefix(timeStr: string): string {
        if (timeStr[0] === '0') {
            return timeStr[1];
        }
        return timeStr;
    }

    /**
     * 文件系统进来，设置按需策略，没数据开关关闭，有数据默认选择第一条
     *
     */
    setOnDemandPolicyConfig(vstoreId: string): void {
        this.service
            .getBatchPolicy(this.type, vstoreId)
            .subscribe(({ data }: { data: OnDemandPolicy[] }) => {
                if (data.length) {
                    this.addPolicyAndSelect(data[0]);
                    this.controlScanSwitch(true);
                } else {
                    this.controlScanSwitch(false);
                }
            });
    }

    /**
     * 文件系统进来，设置实时策略，默认选择default
     *
     */
    setOnAccessPolicyConfig(vstoreId: string): void {
        this.service
            .getBatchPolicy(this.type, vstoreId)
            .subscribe(({ data }: { data: OnAccessPolicy[] }) => {
                // data 里必有一条default
                this.addPolicyAndSelect(data.find((item: OnAccessPolicy) => item.name === DefaultOnAccessPolicyName));
            });
    }


    /**
     * 控制扫描开关
     *
     */
    controlScanSwitch(scanSwitch: boolean): void {
        this.switch = scanSwitch;
        this.onSwitchChange(scanSwitch);
    }

    ngOnDestroy(): void {
        this.vm.removeControl(`selectedScanPolicy${this.type}`);

        if (this.type === ScanPolicyTypeE.ONDEMAND && this.entry === ScanMissionEntryTypeE.CREATE) {
            ['immediately', 'durationType', 'durationHour', 'durationMinute'].forEach((controlName: string) => {
                this.vm.removeControl(controlName);
            });
        }
    }

    /**
     * 点击加载“不扫描文件类型/路径”的详情页
     */
    openDetailWindow(policy: ScanPolicyTypeE, tableType: ExcludeFileE): void {
        const tableId = `fs-scan-policy-card-${policy}-${tableType}-table`;
        this.lazyloadService.appendComponent(TypePathDetailTableComponent, {
            tableType,
            tableId,
            isSlideWindow: true,
            selectPolicy: this.selectPolicyOpt.data,
        });
    }
}
